import React from 'react';
import {
    ErrorListProps,
    FormItemProps,
    FormProps,
    FormInstance,
    Rule,
    RuleObject,
    RuleRender,
} from 'antd/lib/form';
import { Form } from 'antd';
import classNames from 'classnames';
import './VtxForm.scss';
import { IAutomatable, preventDataAutomationOverriding } from '../../helpers/IAutomatable';
export {
    ErrorListProps as VtxErrorListProps,
    FormInstance as VtxFormInstance,
    Rule as VtxRule,
    RuleObject as VtxRuleObject,
    RuleRender as VtxRender,
};

export interface VtxFormProps<Values extends object = never>
    extends Omit<FormProps<Values>, 'requiredMark' | 'form'>,
        IAutomatable {
    /**
     * Form control instance created by useForm(). By passing in this reference, you gain greater type-safety
     */
    form: FormInstanceStrict<Values>;
}

// TODO: remove this type once https://github.com/ant-design/ant-design/issues/29315 is fixed and closed
export declare type ShouldUpdate<Values = unknown> =
    | boolean
    | ((
          prevValues: Values,
          nextValues: Values,
          info: {
              source?: string;
          }
      ) => boolean);

export interface VtxFormItemProps<FormKeysAndValues extends object = never>
    extends Omit<FormItemProps<FormKeysAndValues>, 'name'>,
        IAutomatable {
    /**
     * The name of one of the keys in the data model for the form. If you are getting the error "Type 'string' is not assignable to type 'undefined'" then please make sure to explicitly pass the type parameter to the JSX tag
     */
    name?: keyof ObjNotArray<FormKeysAndValues>;
    /**
     * Custom field update logic. Read more at: https://ant.design/components/form/#shouldUpdate
     */
    shouldUpdate?: ShouldUpdate<FormKeysAndValues>;
}

// TODO: memoize this once the useMemo generic bug is fixed: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087
export function VtxForm<Values extends object>(props: VtxFormProps<Values>) {
    const automationClass = 'vtx-form';
    const { className, layout, form, ...otherProps } = preventDataAutomationOverriding(props);
    const classNamesCombined = classNames(automationClass, className);
    return (
        <Form
            data-automation-class={automationClass}
            layout={layout ? layout : 'vertical'}
            className={classNamesCombined}
            form={form as FormInstance}
            {...otherProps}
        >
            {props.children}
        </Form>
    );
}

export type ObjNotArray<T> = T extends Array<unknown> ? never : T;

// TODO: memoize this once the useMemo generic bug is fixed: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087
/**
 * Form field component for data bidirectional binding, validation, layout, and so on.
 */
export const VtxFormItem = <FormKeysAndValues extends object>(
    props: VtxFormItemProps<ObjNotArray<FormKeysAndValues>>
) => {
    const automationClass = 'vtx-form-item';
    const { className, name, ...otherProps } = preventDataAutomationOverriding(props);
    const classNamesCombined = classNames(automationClass, className);

    if (name !== undefined && typeof name !== 'string') {
        throw new TypeError(
            `the name property only supports string values. Instead, the type was ${typeof name}`
        );
    }
    return (
        <Form.Item<ObjNotArray<FormKeysAndValues>>
            data-automation-class={automationClass}
            className={classNamesCombined}
            name={name}
            {...otherProps}
        >
            {props.children}
        </Form.Item>
    );
};

export interface FormInstanceStrict<FormFieldsAndValues>
    extends Omit<FormInstance<FormFieldsAndValues>, 'getFieldValue'> {
    // TODO remove this when (or if) this suggestion is accepted https://github.com/react-component/field-form/issues/262
    getFieldValue: <Key extends keyof FormFieldsAndValues>(name: Key) => FormFieldsAndValues[Key] | undefined;
}

export const VtxFormErrorList = Form.ErrorList;
export const VtxFormProvider = Form.Provider;
export function useForm<FormFieldsAndValues extends object = never>(
    exampleFormFieldsAndValues: FormFieldsAndValues,
    /**
     * If you happen to have an already existing form instance, you can provide one. Otherwise this function will create the form instance and return it to you
     */
    anotherFormInstance?: FormInstance<FormFieldsAndValues>
): [FormInstanceStrict<FormFieldsAndValues>] {
    if (Object.keys(exampleFormFieldsAndValues).length === 0) {
        throw new TypeError('exampleFormFieldsAndValues must have at least one property');
    }
    return Form.useForm<FormFieldsAndValues>(anotherFormInstance) as [
        FormInstanceStrict<FormFieldsAndValues>
    ];
}
